import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, NotebookPen, Pin, PinOff, Plus, Search, StickyNote } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtRelative } from '../../../shared/format'
import { PROJECT_WORK_PERM } from '../../permissions'
import type { Note, Project } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { useDeleteNote, useSaveNote } from '../../workApi'
import { RowMenu } from '../RowMenu'

export function NotesTab({ project }: { project: Project }) {
  const { nameOf } = useStaffLookup()
  const { t } = useTranslation()
  const { can } = useAuth()
  const save = useSaveNote(project.id)
  const remove = useDeleteNote(project.id)
  const [q, setQ] = useState('')
  const [dialog, setDialog] = useState<{ open: boolean; note: Note | null }>({ open: false, note: null })
  const canWrite = can(PROJECT_WORK_PERM)
  const term = q.trim().toLocaleLowerCase('tr')
  const notes = project.notes
    .filter((n) => !term || `${n.title} ${n.body}`.toLocaleLowerCase('tr').includes(term))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.updatedAt.localeCompare(a.updatedAt))

  const togglePin = (n: Note) => save.mutate({ id: n.id, input: { title: n.title, body: n.body, pinned: !n.pinned } }, { onError: toastError })
  const onDelete = async (n: Note) => {
    if (!(await confirmModal({ title: t('projects.notes.deleteTitle'), description: t('projects.notes.deleteBody', { name: n.title }), confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(n.id, { onSuccess: () => toast.success(t('projects.notes.deleted')), onError: toastError })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-40 flex-1 sm:max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('projects.notes.search')} aria-label={t('projects.notes.search')} className="pl-8" />
        </div>
        {canWrite ? (
          <Button className="ml-auto" onClick={() => setDialog({ open: true, note: null })}>
            <Plus />
            {t('projects.notes.add')}
          </Button>
        ) : null}
      </div>
      {notes.length === 0 ? (
        <div className="rounded-xl border bg-card shadow-card">
          <EmptyState icon={StickyNote} title={project.notes.length ? t('projects.notes.noMatch') : t('projects.notes.empty')} description={t('projects.notes.emptyHint')} />
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {notes.map((n) => (
            <li key={n.id} className={cn('flex flex-col rounded-xl border bg-card p-4 shadow-card', n.pinned && 'border-warning/40 bg-warning/[0.05]')}>
              <div className="flex items-start gap-2">
                <h3 className="min-w-0 flex-1 font-semibold">{n.title}</h3>
                {canWrite ? (
                  <>
                    <Button size="icon" variant="ghost" className="size-8" aria-label={t(n.pinned ? 'projects.notes.unpin' : 'projects.notes.pin')} aria-pressed={n.pinned} onClick={() => togglePin(n)}>
                      {n.pinned ? <PinOff /> : <Pin />}
                    </Button>
                    <RowMenu label={n.title} onEdit={() => setDialog({ open: true, note: n })} onDelete={() => void onDelete(n)} />
                  </>
                ) : null}
              </div>
              <p className="mt-2 flex-1 whitespace-pre-wrap break-words text-sm text-muted-foreground">{n.body}</p>
              <p className="mt-3 text-xs text-muted-foreground">{t('projects.notes.meta', { author: nameOf(n.author), when: fmtRelative(n.updatedAt) })}</p>
            </li>
          ))}
        </ul>
      )}
      {dialog.open ? <NoteForm key={dialog.note?.id ?? 'new'} project={project} note={dialog.note} onDone={() => setDialog({ open: false, note: null })} /> : null}
    </div>
  )
}

function NoteForm({ project, note, onDone }: { project: Project; note: Note | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveNote(project.id)
  const schema = z.object({
    title: z.string().trim().min(2, t('projects.notes.titleRequired')).max(80),
    body: z.string().trim().min(1, t('projects.notes.bodyRequired')).max(5000),
    pinned: z.boolean(),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { title: note?.title ?? '', body: note?.body ?? '', pinned: note?.pinned ?? false } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await save.mutateAsync({ id: note?.id, input })
      toast.success(t(note ? 'projects.notes.updated' : 'projects.notes.created'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<NotebookPen />}
      title={t(note ? 'projects.notes.editTitle' : 'projects.notes.createTitle')}
      description={t('projects.notes.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="nt-title">{t('projects.notes.title')}</Label>
          <Input id="nt-title" aria-invalid={!!errors.title} {...form.register('title')} />
          {errors.title ? <p className="text-xs text-destructive">{errors.title.message}</p> : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="nt-body">{t('projects.notes.body')}</Label>
          <Textarea id="nt-body" rows={10} aria-invalid={!!errors.body} {...form.register('body')} />
          {errors.body ? <p className="text-xs text-destructive">{errors.body.message}</p> : null}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" className="size-4 accent-[var(--color-primary)]" {...form.register('pinned')} />
          {t('projects.notes.pinLabel')}
        </label>
      </div>
    </Modal>
  )
}
